import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from './Header';
import Footer from './Footer';
import SEOHead from './SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Loader2, Copy, CheckCircle, Megaphone, Image as ImageIcon, Link2,
  Twitter, Instagram, Facebook, Send, Mail, Trash2, ExternalLink,
  ArrowLeft, MousePointerClick
} from 'lucide-react';
import { marketingAPI } from '../services/marketingApi';
import { affiliateAuth } from '../services/affiliateApi';

const PLATFORM_ICONS = { twitter: Twitter, instagram: Instagram, facebook: Facebook, telegram: Send, email: Mail };

const AffiliateMarketingKitPage = () => {
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [copy, setCopy] = useState(null);
  const [banners, setBanners] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState({});

  // short link form
  const [campaign, setCampaign] = useState('');
  const [source, setSource] = useState('twitter');
  const [medium, setMedium] = useState('social');
  const [targetPath, setTargetPath] = useState('/products');
  const [creatingLink, setCreatingLink] = useState(false);

  const t = (en, es, ru) => currentLanguage === 'es' ? es : currentLanguage === 'ru' ? ru : en;

  useEffect(() => {
    if (!affiliateAuth.getToken()) {
      navigate('/affiliate/login');
      return;
    }
    (async () => {
      try {
        const [c, b, l] = await Promise.all([
          marketingAPI.getCopy(),
          marketingAPI.getBanners(),
          marketingAPI.getShortLinks(),
        ]);
        setCopy(c);
        setBanners(b);
        setLinks(l.links || []);
      } catch (err) {
        if (err.status === 401 || err.status === 403) {
          affiliateAuth.clear();
          navigate('/affiliate/login');
          return;
        }
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [key]: false })), 1500);
    } catch {}
  };

  const handleCreateLink = async (e) => {
    e.preventDefault();
    if (!campaign.trim()) return;
    setCreatingLink(true);
    try {
      const res = await marketingAPI.createShortLink({
        campaign: campaign.trim(),
        source,
        medium,
        target_path: targetPath,
      });
      setLinks(prev => [{ ...res, short_url: res.short_url }, ...prev]);
      setCampaign('');
    } catch (err) {
      setError(err.message);
    } finally {
      setCreatingLink(false);
    }
  };

  const handleDeleteLink = async (slug) => {
    if (!window.confirm(t('Delete this short link?', '¿Eliminar este enlace?', 'Удалить эту ссылку?'))) return;
    try {
      await marketingAPI.deleteShortLink(slug);
      setLinks(prev => prev.filter(l => l.slug !== slug));
    } catch (err) { setError(err.message); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-capsule-bg flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-capsule-teal animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-capsule-bg">
      <SEOHead page="affiliate" customTitle="Marketing Kit | PenLab Peptide Affiliate" />
      <Header />
      <main className="container mx-auto px-4 py-8 md:py-12" data-testid="marketing-kit-page">
        <div className="flex items-center justify-between mb-6">
          <Link to="/affiliate/dashboard" className="inline-flex items-center text-capsule-teal hover:underline text-sm">
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('Back to dashboard', 'Volver al panel', 'К панели')}
          </Link>
          {copy?.promo_code && (
            <Badge className="bg-capsule-teal text-white">
              {t('Code', 'Código', 'Код')}: {copy.promo_code}
            </Badge>
          )}
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-capsule-teal to-capsule-aqua mb-4 shadow-teal-glow">
            <Megaphone className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {t('Marketing Kit', 'Kit de Marketing', 'Маркетинговый набор')}
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            {t('Ready-to-use copy, banners, and tracked short links to share on any platform.',
               'Texto, banners y enlaces cortos listos para compartir en cualquier plataforma.',
               'Готовые тексты, баннеры и отслеживаемые короткие ссылки для любой платформы.')}
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6" data-testid="kit-error">
            {error}
          </div>
        )}

        <Tabs defaultValue="copy" className="w-full">
          <TabsList className="grid w-full grid-cols-3 max-w-2xl mx-auto mb-6">
            <TabsTrigger value="copy" data-testid="tab-copy">
              <Send className="w-4 h-4 mr-2" /> {t('Copy & Posts', 'Textos', 'Тексты')}
            </TabsTrigger>
            <TabsTrigger value="banners" data-testid="tab-banners">
              <ImageIcon className="w-4 h-4 mr-2" /> {t('Banners', 'Banners', 'Баннеры')}
            </TabsTrigger>
            <TabsTrigger value="links" data-testid="tab-links">
              <Link2 className="w-4 h-4 mr-2" /> {t('Short Links', 'Enlaces', 'Ссылки')}
            </TabsTrigger>
          </TabsList>

          {/* COPY TAB */}
          <TabsContent value="copy" className="space-y-4">
            {copy?.templates && Object.entries(copy.templates).map(([platform, byLang]) => {
              const Icon = PLATFORM_ICONS[platform] || Send;
              const posts = byLang[currentLanguage] || byLang.en || [];
              return (
                <Card key={platform} className="capsule-card" data-testid={`copy-platform-${platform}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 capitalize">
                      <Icon className="w-5 h-5 text-capsule-teal" />
                      {platform}
                      <Badge variant="secondary" className="ml-auto">{posts.length}</Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {posts.map((post, i) => {
                      const key = `${platform}-${i}`;
                      return (
                        <div key={key} className="bg-gray-50 border rounded-lg p-4 relative" data-testid={`copy-${platform}-${i}`}>
                          <pre className="whitespace-pre-wrap text-sm text-gray-800 font-sans leading-relaxed pr-12">{post}</pre>
                          <Button size="sm" variant="outline" className="absolute top-3 right-3"
                            onClick={() => copyText(post, key)}
                            data-testid={`copy-btn-${platform}-${i}`}>
                            {copied[key] ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
          </TabsContent>

          {/* BANNERS TAB */}
          <TabsContent value="banners" className="space-y-4">
            <Card className="capsule-card">
              <CardHeader>
                <CardTitle>{t('Banner Library', 'Banners', 'Библиотека баннеров')}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  {t('Click any banner to copy your tracked referral URL. Right-click → Save image to download.',
                     'Haz clic en cualquier banner para copiar tu URL. Clic derecho → Guardar imagen.',
                     'Кликните, чтобы скопировать ссылку. Правый клик → Сохранить.')}
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(banners?.banners || []).map((b) => (
                    <div key={b.slug} className="border rounded-2xl overflow-hidden hover:shadow-lg transition-all" data-testid={`banner-${b.slug}`}>
                      <div className="bg-gradient-to-br from-capsule-teal/10 via-white to-capsule-aqua/10 aspect-[16/9] flex items-center justify-center">
                        <div className="text-center px-4">
                          <ImageIcon className="w-10 h-10 text-capsule-teal mx-auto mb-2" />
                          <p className="text-xs text-gray-500">{b.size}</p>
                        </div>
                      </div>
                      <div className="p-4">
                        <p className="font-medium text-sm text-gray-900 mb-2">{b.label}</p>
                        <div className="flex gap-2">
                          <Button size="sm" variant="outline" className="flex-1"
                            onClick={() => copyText(b.referral_link, `banner-${b.slug}`)}
                            data-testid={`copy-banner-link-${b.slug}`}>
                            {copied[`banner-${b.slug}`] ? <CheckCircle className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
                            {t('Copy URL', 'Copiar URL', 'Копировать')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  {t('Banner image files coming soon, referral URLs are active now.',
                     'Imágenes de banners próximamente, las URLs ya funcionan.',
                     'Изображения баннеров скоро, ссылки уже работают.')}
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* SHORT LINKS TAB */}
          <TabsContent value="links" className="space-y-4">
            <Card className="capsule-card">
              <CardHeader>
                <CardTitle>{t('Create UTM-tracked Short Link', 'Crear Enlace Corto UTM', 'Создать короткую ссылку UTM')}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateLink} className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="campaign">{t('Campaign name', 'Campaña', 'Кампания')} *</Label>
                    <Input id="campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)}
                      placeholder="spring2026" required data-testid="link-campaign-input" />
                  </div>
                  <div>
                    <Label htmlFor="source">{t('Source', 'Fuente', 'Источник')}</Label>
                    <select id="source" className="w-full h-10 border rounded-md px-3 mt-1"
                      value={source} onChange={(e) => setSource(e.target.value)} data-testid="link-source-select">
                      <option value="twitter">Twitter / X</option>
                      <option value="instagram">Instagram</option>
                      <option value="facebook">Facebook</option>
                      <option value="telegram">Telegram</option>
                      <option value="email">Email</option>
                      <option value="banner">Banner</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="medium">{t('Medium', 'Medio', 'Канал')}</Label>
                    <select id="medium" className="w-full h-10 border rounded-md px-3 mt-1"
                      value={medium} onChange={(e) => setMedium(e.target.value)} data-testid="link-medium-select">
                      <option value="social">Social</option>
                      <option value="email">Email</option>
                      <option value="banner">Banner</option>
                      <option value="organic">Organic</option>
                      <option value="paid">Paid</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="target">{t('Target page', 'Página destino', 'Целевая страница')}</Label>
                    <select id="target" className="w-full h-10 border rounded-md px-3 mt-1"
                      value={targetPath} onChange={(e) => setTargetPath(e.target.value)} data-testid="link-target-select">
                      <option value="/">{t('Home', 'Inicio', 'Главная')}</option>
                      <option value="/products">{t('Products', 'Productos', 'Товары')}</option>
                      <option value="/about">{t('About', 'Acerca', 'О нас')}</option>
                      <option value="/blog">{t('Blog', 'Blog', 'Блог')}</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <Button type="submit" disabled={creatingLink || !campaign.trim()}
                      className="bg-gradient-to-r from-capsule-teal to-capsule-aqua text-white"
                      data-testid="create-short-link-btn">
                      {creatingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : t('Create Short Link', 'Crear Enlace', 'Создать ссылку')}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            <Card className="capsule-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-capsule-teal" />
                  {t('Your Short Links', 'Tus Enlaces', 'Ваши ссылки')}
                  <Badge variant="secondary" className="ml-auto">{links.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {links.length === 0 ? (
                  <p className="text-center text-gray-500 py-6" data-testid="no-short-links">
                    {t('No short links yet. Create one above.',
                       'Aún no hay enlaces. Crea uno arriba.',
                       'Пока нет ссылок. Создайте выше.')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {links.map((l) => (
                      <div key={l.slug} className="border rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid={`short-link-${l.slug}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <code className="font-mono text-sm bg-gray-100 px-2 py-1 rounded truncate">
                              {l.short_url}
                            </code>
                            <Button size="sm" variant="outline" onClick={() => copyText(l.short_url, `link-${l.slug}`)}
                              data-testid={`copy-short-${l.slug}`}>
                              {copied[`link-${l.slug}`] ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                            </Button>
                          </div>
                          <div className="text-xs text-gray-500 flex flex-wrap gap-2">
                            <span><strong>{t('Campaign', 'Campaña', 'Кампания')}:</strong> {l.campaign}</span>
                            {l.source && <span>· {l.source}</span>}
                            {l.medium && <span>· {l.medium}</span>}
                            <span className="inline-flex items-center gap-1">
                              · <MousePointerClick className="w-3 h-3" /> {l.click_count || 0}
                            </span>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <a href={l.short_url} target="_blank" rel="noopener noreferrer">
                            <Button size="sm" variant="outline" data-testid={`test-short-${l.slug}`}>
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                          </a>
                          <Button size="sm" variant="outline"
                            className="text-red-600 hover:bg-red-50"
                            onClick={() => handleDeleteLink(l.slug)}
                            data-testid={`delete-short-${l.slug}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default AffiliateMarketingKitPage;
